import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { getData } from '../../services/api';
import AuthProductBrand from './AuthProductBrand';

const VARIANTS = {
  login: {
    tag: 'About Top Skilled Academy',
    title: 'Practical skills for real careers.',
    lead: 'Build industry-ready skills through guided courses, practical training and career support.',
    features: [
      { icon: 'bi-journal-code', title: 'Industry-ready courses', text: 'Learn current skills with structured guidance.' },
      { icon: 'bi-camera-video-fill', title: 'Flexible learning', text: 'Join live classes or learn at your own pace.' },
      { icon: 'bi-kanban-fill', title: 'Projects and internships', text: 'Build practical experience for your portfolio.' },
      { icon: 'bi-briefcase-fill', title: 'Career support', text: 'Prepare for resumes, interviews and opportunities.' },
    ],
  },
  register: {
    tag: 'Create Organization',
    title: 'Spin up your own workspace in minutes.',
    lead: 'Pick your industry, plan and admin account. Modules, roles and tenant isolation are handled for you.',
    features: [
      { icon: 'bi-buildings-fill', title: 'Multi-tenant', text: 'Your data stays isolated end-to-end.' },
      { icon: 'bi-puzzle-fill', title: 'Modular', text: 'Enable only the modules you need.' },
      { icon: 'bi-currency-rupee', title: 'Flexible pricing', text: 'Scale with monthly or yearly plans.' },
      { icon: 'bi-headset', title: 'Real support', text: 'Onboarding help when your team needs it.' },
    ],
  },
  'user-register': {
    tag: 'Top Skilled Services',
    title: 'Start learning with Skill Share.',
    lead: 'Choose your college or register individually, verify your account and begin building skills for your next opportunity.',
    features: [
      { icon: 'bi-play-circle-fill', title: 'Courses that fit', text: 'Explore guided learning across in-demand skills.' },
      { icon: 'bi-people-fill', title: 'Live expert sessions', text: 'Learn directly through interactive classes.' },
      { icon: 'bi-laptop-fill', title: 'Practical experience', text: 'Apply concepts through projects and internships.' },
      { icon: 'bi-graph-up-arrow', title: 'Career growth', text: 'Track progress and prepare for employment.' },
    ],
  },
  'college-register': {
    tag: 'Skill Share College Access',
    title: 'Build skills beyond the classroom.',
    lead: 'Register through your college partnership and access the learning opportunities configured for your institution.',
    features: [
      { icon: 'bi-mortarboard-fill', title: 'College learning access', text: 'Use benefits available through your institution.' },
      { icon: 'bi-patch-check-fill', title: 'Verified registration', text: 'Keep access correctly linked to your college.' },
      { icon: 'bi-journal-richtext', title: 'Skill-based courses', text: 'Develop practical and career-focused knowledge.' },
      { icon: 'bi-briefcase-fill', title: 'Career preparation', text: 'Move from learning toward real opportunities.' },
    ],
  },
  'sa-login': {
    tag: 'Platform Operations',
    title: 'Run the platform with confidence.',
    lead: 'Super admin console for tenants, plans, modules, audits and platform configuration.',
    features: [
      { icon: 'bi-shield-shaded', title: 'Tenant control', text: 'Provision, suspend and audit any tenant.' },
      { icon: 'bi-diagram-3-fill', title: 'Module governance', text: 'Toggle modules per industry and plan.' },
      { icon: 'bi-bar-chart-line', title: 'Platform health', text: 'Track usage across every workspace.' },
      { icon: 'bi-receipt', title: 'Billing and plans', text: 'Manage quotas, renewals and overrides.' },
    ],
  },
};

export default function AuthHero({ variant = 'login' }) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [collegeState, setCollegeState] = useState({ loading: false, college: null, tenant: null });

  const pathSlug = useMemo(() => {
    const parts = String(location.pathname || '').split('/').map((part) => part.trim()).filter(Boolean);
    if (parts.length !== 1) return '';
    const candidate = parts[0].toLowerCase();
    if (['login', 'register', 'sa'].includes(candidate)) return '';
    return candidate;
  }, [location.pathname]);

  const tenantCode = searchParams.get('tenant') || searchParams.get('code') || '';
  const collegeCode = searchParams.get('college') || searchParams.get('collegeCode') || '';

  useEffect(() => {
    let active = true;

    const loadCollege = async () => {
      const shouldUseSlug = variant === 'college-register' && pathSlug;
      const shouldUseQueryLookup = variant === 'user-register' && tenantCode && collegeCode;
      if (!shouldUseSlug && !shouldUseQueryLookup) {
        setCollegeState({ loading: false, college: null, tenant: null });
        return;
      }

      setCollegeState((prev) => ({ ...prev, loading: true }));
      try {
        const response = shouldUseSlug
          ? await getData(`/training/college-access/public/resolve-by-slug?collegeSlug=${encodeURIComponent(pathSlug)}`)
          : await getData(`/auth/resolve-college-access?organizationCode=${encodeURIComponent(tenantCode)}&collegeCode=${encodeURIComponent(collegeCode)}`);
        const payload = response?.data || response || {};
        if (!active) return;
        setCollegeState({ loading: false, college: payload.college || null, tenant: payload.tenant || null });
      } catch (_) {
        if (!active) return;
        setCollegeState({ loading: false, college: null, tenant: null });
      }
    };

    loadCollege();
    return () => { active = false; };
  }, [variant, pathSlug, tenantCode, collegeCode]);

  const college = collegeState.college;
  const tenant = collegeState.tenant;
  const hero = useMemo(() => {
    if (!college) return VARIANTS[variant] || VARIANTS.login;
    return {
      tag: 'Skill Share College Access',
      title: college.name || 'College registration portal',
      lead: college.description || `Register through the ${tenant?.name || 'college'} access portal to activate eligible learning access.`,
      features: [
        { icon: 'bi-globe2', title: 'Website', text: college.websiteUrl || 'Website details can be configured for this college.' },
        { icon: 'bi-telephone-fill', title: 'Support contact', text: college.contactNumber || 'Contact details will be shared by the college coordinator.' },
        { icon: 'bi-patch-check-fill', title: 'Verification flow', text: college.supportMessage || 'Registration is validated through college access controls before activation.' },
        { icon: 'bi-stars', title: 'Learning opportunities', text: 'Authorized students can explore the courses and services available through their college.' },
      ],
    };
  }, [college, tenant, variant]);

  return (
    <aside className={`auth-hero auth-hero--${variant}`} aria-hidden={variant === 'login' ? undefined : true}>
      <div className="hero-brand">
        <AuthProductBrand compact />
      </div>

      <div>
        <div className="hero-tagline">{hero.tag}</div>
        <h1>{hero.title}</h1>
        <p className="hero-lead">{hero.lead}</p>

        <div className="hero-features">
          {hero.features.map((feature, index) => (
            <div className="hero-feature" key={`${feature.title}-${index}`}>
              <span className="feat-icon"><i className={`bi ${feature.icon}`}></i></span>
              <span>
                <span className="feat-title">{feature.title}</span>
                {variant !== 'login' && <span>{feature.text}</span>}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="hero-footer">
        <span>{college ? 'College Registration Portal' : `${new Date().getFullYear()} Skill Share`}</span>
        <span>·</span>
        <span>{college ? (tenant?.name || 'Learning Access') : 'by Top Skilled Academy'}</span>
      </div>
    </aside>
  );
}

